<template>
  <Teleport to="body">
    <div
      ref="popupEl"
      class="confirm-popup"
      :style="positionStyle"
      tabindex="-1"
      @keydown.esc="$emit('cancel')"
      @keydown.enter="$emit('confirm')"
    >
      <div class="confirm-popup__title">{{ title }}</div>
      <!-- questionHtml: заранее экранированная строка с жирными фрагментами (имя/число); используется вместо question+highlight. -->
      <div v-if="questionHtml" class="confirm-popup__question" v-html="questionHtml"></div>
      <div v-else class="confirm-popup__question">
        {{ question }}<template v-if="highlight">&nbsp;<span class="confirm-popup__highlight">{{ highlight }}</span></template>
      </div>
      <div class="confirm-popup__actions">
        <button class="confirm-popup__cancel" type="button" @click="$emit('cancel')">{{ cancelLabel ?? t('cancel_short') }}</button>
        <button class="confirm-popup__confirm" type="button" @click="$emit('confirm')">{{ confirmLabel ?? t('confirm_short') }}</button>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick } from 'vue';
import { i18n } from '@/i18n';

const t = (key: string) => i18n.global.t(key);

const props = withDefaults(defineProps<{
  title: string;
  question: string;
  highlight?: string;
  /** Готовый HTML вопроса (с жирными фрагментами). Должен быть заранее экранирован вызывающей стороной. */
  questionHtml?: string;
  position: { x: number; y: number };
  confirmLabel?: string;
  cancelLabel?: string;
  width?: number;
}>(), {
  width: 240,
});

const emit = defineEmits<{
  (e: 'confirm'): void;
  (e: 'cancel'): void;
}>();

const popupEl = ref<HTMLElement | null>(null);
const clampedPos = ref({ x: props.position.x, y: props.position.y });

function clampPosition() {
  const margin = 8;
  const popupWidth = popupEl.value?.offsetWidth ?? props.width;
  const popupHeight = popupEl.value?.offsetHeight ?? 120;
  const maxX = Math.max(margin, window.innerWidth - popupWidth - margin);
  const maxY = Math.max(margin, window.innerHeight - popupHeight - margin);
  clampedPos.value = {
    x: Math.min(Math.max(props.position.x, margin), maxX),
    y: Math.min(Math.max(props.position.y, margin), maxY),
  };
}

const positionStyle = computed(() => {
  const width = `${props.width}px`;
  return {
    left: clampedPos.value.x + 'px',
    top: clampedPos.value.y + 'px',
    width,
    minWidth: width,
    maxWidth: width,
  };
});

function onDocumentMousedown(e: MouseEvent) {
  if (popupEl.value && !popupEl.value.contains(e.target as Node)) {
    emit('cancel');
  }
}

onMounted(() => {
  nextTick(() => clampPosition());
  popupEl.value?.focus();
  document.addEventListener('mousedown', onDocumentMousedown, true);
  window.addEventListener('resize', clampPosition, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocumentMousedown, true);
  window.removeEventListener('resize', clampPosition, true);
});

watch(
  () => props.position,
  () => nextTick(() => clampPosition()),
  { deep: true }
);
</script>

<style scoped lang="scss">
.confirm-popup {
  position: fixed;
  z-index: 10050;
  display: flex;
  box-sizing: border-box;
  padding: 12px;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  border-radius: 12px;
  border: 1px solid #F2F2F2;
  background: #FFF;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.20);
  outline: none;

  &__title {
    color: #4D4D4D;
    font-family: Roboto, sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: normal;
  }

  &__question {
    color: #4D4D4D;
    text-align: center;
    font-family: Roboto, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
  }

  &__highlight {
    font-weight: 600;
    line-height: normal;
  }

  &__actions {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
  }

  &__cancel {
    display: flex;
    height: 32px;
    padding: 0 16px;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: var(--prompt-secondary-bg, #FFFFFF);
    color: var(--Text-color-text-secondary, #979797);
    font-family: Roboto, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 18px;
    cursor: pointer;
    user-select: none;
    border: none;

    &:hover {
      background: var(--prompt-secondary-hover-bg, #F2F2F2);
    }

    &:active {
      background: var(--prompt-secondary-active-bg, #E8E8E8);
    }
  }

  &__confirm {
    display: flex;
    height: 32px;
    padding: 0 16px;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: var(--prompt-primary-bg, #3F884F);
    box-shadow: 0 -2px 0 0 var(--prompt-primary-inset, #377444) inset;
    color: var(--Text-color-text-btn, #FFF);
    font-family: Roboto, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 18px;
    border: none;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: var(--prompt-primary-hover-bg, #377444);
    }

    &:active:not(:disabled) {
      background: var(--prompt-primary-active-bg, #2F673D);
    }

    &:disabled {
      background: var(--prompt-primary-disabled-bg, #AEC9B4);
      box-shadow: 0 -2px 0 0 var(--prompt-primary-disabled-inset, #A2BCA8) inset;
      cursor: default;
    }
  }
}
</style>

